import {
  ReactElement, useCallback, useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useToast } from '~/contexts/newToastContext';
import { UpgradeModule } from '~/pages/NewOverview/components/UpgradeModule';
import { useFilterContext } from '~/pages/NewOverview/contexts/FilterContext';
import { getWaterOverviewInfo } from '~/pages/NewOverview/helpers/overview';
import { WaterOverviewInfo } from '~/providers/types/api-private';
import { WaterContainer, WaterContainerLoading } from '../styles';
import { WaterCardContent } from './CardContent';

interface WaterCardProps {
  expanded: boolean;
  loading: boolean;
  order: number;
  hasWater: boolean;
}

export function WaterCard({
  expanded,
  hasWater,
  order,
  loading,
}: Readonly<WaterCardProps>): ReactElement {
  const { client, selectedOptions, filtersDependencies } = useFilterContext();
  const [loadingData, setLoadingData] = useState(false);
  const [waterData, setWaterData] = useState<WaterOverviewInfo>();
  const { toast } = useToast();
  const { t } = useTranslation();

  const generalLoading = useMemo(() => loading || loadingData, [loading, loadingData]);

  const getWaterInfo = useCallback(async (): Promise<void> => {
    setLoadingData(true);

    const data = await getWaterOverviewInfo(Number(client), {
      stateIds: selectedOptions.state,
      unitIds: selectedOptions.group?.units,
      healthStatus: selectedOptions.healthStatus,
      thermalComfortStatus: selectedOptions.status,
    });

    if (data) {
      setWaterData(data);
    } else {
      toast(t('erroTitulo'), t('erroBuscaDadosAgua'), 'error');
    }
    setLoadingData(false);
  }, [filtersDependencies]);

  useEffect(() => {
    if (client && hasWater && !loading) {
      getWaterInfo();
    }
  }, [getWaterInfo, hasWater, loading, ...filtersDependencies]);

  useEffect(() => {
    if (!hasWater || loading) {
      setWaterData(undefined);
      setLoadingData(false);
    }
  }, [hasWater, loading]);

  if (generalLoading) {
    return (
      <WaterContainerLoading style={{ order }}>
        <Skeleton
          height="360px"
          borderRadius="8px"
          className="skeleton-span"
          containerClassName="skeleton-container-span"
        />
      </WaterContainerLoading>
    );
  }

  return (
    <WaterContainer
      expanded={expanded}
      style={{ order }}
    >
      {!hasWater && !loading && (<UpgradeModule module="moreModules" />)}
      <WaterCardContent data={hasWater ? waterData : undefined} expanded={expanded} />
    </WaterContainer>
  );
}
